import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button, OverlayTrigger, Tooltip, Alert, Modal } from 'react-bootstrap';
import { BiEdit, BiTrash, BiSearch, BiX } from 'react-icons/bi';
import './Users.scss';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (search) {
      setFilteredUsers(users.filter(user => 
        user.user_name.toLowerCase().includes(search.toLowerCase()) || 
        `${user.user_last_name}, ${user.user_first_name}`.toLowerCase().includes(search.toLowerCase())
      ));
    } else {
      setFilteredUsers(users);
    }
  }, [search, users]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users-api/users');

      // Fetch permissions for each user
      const usersWithPermissions = await Promise.all(response.data.map(async (user) => {
        const permissionsResponse = await axios.get(`/users-api/user/${user.user_id}/permissions`);
        return {
          ...user,
          permissions: permissionsResponse.data.map(permission => permission.perm_name)
        };
      }));

      setUsers(usersWithPermissions);
      setFilteredUsers(usersWithPermissions);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/users-api/user/${selectedUser.user_id}`);
      setSuccessMessage('User successfully deleted');
      fetchUsers();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearch('');
  };

  return (
    <div className="users-container">
      <h2>Users</h2>
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </Alert>
      )}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by User or Name"
          value={search}
          onChange={handleSearchChange}
        />
        <OverlayTrigger
          overlay={<Tooltip id="tooltip-search">Search User</Tooltip>}
        >
          <Button variant="primary" className="search-button">
            <BiSearch />
          </Button>
        </OverlayTrigger>
        <OverlayTrigger
          overlay={<Tooltip id="tooltip-clear-search">Clear Search Criteria</Tooltip>}
        >
          <Button variant="secondary" className="clear-button" onClick={clearSearch}>
            <BiX />
          </Button>
        </OverlayTrigger>
      </div>
      {filteredUsers.length === 0 ? (
        <p>No users found matching search criteria</p>
      ) : (
        <table className="table table-striped">
          <thead>
            <tr>
              <th>User</th>
              <th>Status</th>
              <th>Name</th>
              <th>Permissions</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.user_id}>
                <td>{user.user_name}</td>
                <td>{user.user_active === 1 ? 'Active' : 'Inactive'}</td>
                <td>{`${user.user_last_name}, ${user.user_first_name}`}</td>
                <td>{user.permissions.join(', ')}</td>
                <td>{new Date(user.user_creation_date).toLocaleDateString()}</td>
                <td>
                  <OverlayTrigger
                    overlay={<Tooltip id={`tooltip-edit-${user.user_id}`}>Edit this user</Tooltip>}
                  >
                    <Button variant="warning" className="action-button" as={Link} to={`/users/edit/${user.user_id}`}>
                      <BiEdit />
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    overlay={<Tooltip id={`tooltip-delete-${user.user_id}`}>Delete this user</Tooltip>}
                  >
                    <Button variant="danger" className="action-button" onClick={() => handleDelete(user)}>
                      <BiTrash />
                    </Button>
                  </OverlayTrigger>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this user?</p>
          {selectedUser && (
            <div>
              <p><strong>User:</strong> {selectedUser.user_name}</p>
              <p><strong>Name:</strong> {`${selectedUser.user_last_name}, ${selectedUser.user_first_name}`}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Users;

